import { createStyles, WithStyles } from '@material-ui/core'
import withStyles from '@material-ui/core/styles/withStyles'
import React from 'react'
import { applyToPoint, identity, Matrix, scale, transform, translate } from 'transformation-matrix'
import classNames from 'utils/classNames'
import { vectorAdd, vectorSubtract } from 'utils/mathUtils'
import { throttleRaf } from 'utils/throttleRaf'

const scrollZoomSpeed = 500

const styles = createStyles({
  root: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
  },
  translateBase: {
    display: 'flex',
    flex: 1,
  },
})

// Add global style to prevent text selection during panning
const style = document.createElement('style')
style.textContent = `
  .no-select {
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
  }
  
  /* Style for middle-click panning cursor */
  body.middle-panning * {
    cursor: grabbing !important;
  }
`
document.head.appendChild(style)

interface Props extends WithStyles<typeof styles> {
  initialScale: number
  initialTranslate: [number, number?]
  className?: string
  style?: React.CSSProperties
  isPanning?: boolean
}

interface State {
  matrix: Matrix
  isMiddleClickPanning: boolean
}

function calculateOffset(element: HTMLElement): number[] {
  const offset = [element.offsetLeft, element.offsetTop]
  return element.offsetParent
    ? vectorAdd(offset, calculateOffset(element.offsetParent as HTMLElement))
    : offset
}

class Zoomable extends React.PureComponent<Props, State> {
  static defaultProps = {
    initialScale: 1,
    initialTranslate: [0, 0],
    isPanning: false,
  }

  childRef = React.createRef<HTMLDivElement>()
  panReferencePoint: number[] | null = null

  updateZoom = throttleRaf((event: React.WheelEvent) => {
    const { matrix } = this.state
    let childOffset: number[] = []
    if (this.childRef.current) {
      childOffset = calculateOffset(this.childRef.current)
    }

    // @ts-ignore
    const childCenter = vectorAdd(childOffset, applyToPoint(matrix, [0, 0]))
    const scaleMatrix = scale(1 + event.deltaY / scrollZoomSpeed)
    const mouseInitial = vectorSubtract([event.clientX, event.clientY], childCenter)
    const mouseScaled = applyToPoint(scaleMatrix, mouseInitial)
    const mouseDiff: [number, number] = vectorSubtract(mouseInitial, mouseScaled)
    const newMatrix = transform(translate(...mouseDiff), matrix, scaleMatrix)

    this.setState({ matrix: newMatrix })
  })

  handleMouseMove = throttleRaf((event: React.MouseEvent) => {
    // Only process mouse move if we have a reference point
    if (this.panReferencePoint) {
      // If we're panning, prevent default browser behavior
      event.preventDefault()
      event.stopPropagation()
      
      const currentPoint = [event.clientX, event.clientY]
      const translateAmount: [number, number?] = vectorSubtract(
        currentPoint,
        this.panReferencePoint
      )
      this.panReferencePoint = currentPoint

      const { matrix } = this.state
      const newMatrix = transform(translate(...translateAmount), matrix)
      this.setState({ matrix: newMatrix })
      
      // Add a special CSS class to prevent text selection during panning
      document.body.classList.add('no-select')
    } else {
      document.body.classList.remove('no-select')
    }
  })

  constructor(props: Props) {
    super(props)
    const { initialScale, initialTranslate } = this.props
    this.state = {
      matrix: transform(identity(), translate(...initialTranslate), scale(initialScale)),
      isMiddleClickPanning: false,
    }

    // Add global mouse up handler for safety
    this.handleGlobalMouseUp = this.handleGlobalMouseUp.bind(this)
  }

  // Reference to our actual event handlers for document level events
  documentMouseMoveHandler: ((e: MouseEvent) => void) | null = null
  documentMouseUpHandler: ((e: MouseEvent) => void) | null = null

  componentDidMount() {
    // Add handlers to ensure we clean up even if mouse leaves the window
    window.addEventListener('mouseup', this.handleGlobalMouseUp)
  }

  componentWillUnmount() {
    window.removeEventListener('mouseup', this.handleGlobalMouseUp)
    
    // Make sure we clean up document level handlers
    if (this.documentMouseMoveHandler) {
      document.removeEventListener('mousemove', this.documentMouseMoveHandler)
    }
    if (this.documentMouseUpHandler) {
      document.removeEventListener('mouseup', this.documentMouseUpHandler)
    }
    
    document.body.classList.remove('no-select')
    document.body.classList.remove('middle-panning')
  }

  handleGlobalMouseUp = () => {
    if (this.state.isMiddleClickPanning) {
      this.setState({ isMiddleClickPanning: false })
      document.body.classList.remove('no-select')
      document.body.classList.remove('middle-panning')
      
      // Clean up document handlers
      if (this.documentMouseMoveHandler) {
        document.removeEventListener('mousemove', this.documentMouseMoveHandler)
        this.documentMouseMoveHandler = null
      }
      
      if (this.documentMouseUpHandler) {
        document.removeEventListener('mouseup', this.documentMouseUpHandler)
        this.documentMouseUpHandler = null
      }
    }
    this.panReferencePoint = null
  }

  handleScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    // event.preventDefault();
    this.updateZoom(event)
  }

  handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    // If we were panning with middle-click, prevent event propagation
    if (this.state.isMiddleClickPanning || (this.panReferencePoint && event.button === 1)) {
      event.stopPropagation()
      event.preventDefault()
      
      this.setState({ isMiddleClickPanning: false })
    }
    this.panReferencePoint = null
  }

  handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button === 2) {
      // ignore right click
      return
    }
    
    // Always enable panning with middle mouse button (button 1)
    if (event.button === 1) {
      console.log('Zoomable: middle-click panning')
      this.panReferencePoint = [event.clientX, event.clientY]
      
      // Just set the pan reference point and the React state will handle the rest
      // The tools have been modified to ignore middle mouse events
    }
    
    // Regular left-click behavior (when no tool is active)
    if (event.button === 0) {
      console.log('Zoomable: mouse down')
      this.panReferencePoint = [event.clientX, event.clientY]
    }
  }

  render() {
    const { style, className, classes, children } = this.props
    const { matrix, isMiddleClickPanning } = this.state
    
    // Apply custom styles if middle-click panning is active
    const customStyle = style
    
    const childrenWithMatrix = React.Children.map(children, child => {
      // Only pass the matrix to children, not the isPanning state
      return React.cloneElement(child as React.ReactElement<any>, { matrix })
    })
    
    return (
      <div
        role="presentation"
        style={customStyle}
        className={classNames(className, classes.root)}
        onWheel={this.handleScroll}
        onMouseDown={this.handleMouseDown}
        onMouseUp={this.handleMouseUp}
        onContextMenu={e => isMiddleClickPanning && e.preventDefault()}
        onMouseMove={e => {
            // We only use this for left-click (button 0) dragging
            // Middle-click (button 1) dragging is handled at document level
            if (this.panReferencePoint && !this.state.isMiddleClickPanning) {
              e.preventDefault()
              e.stopPropagation()
              this.handleMouseMove(e)
            }
          }}
      >
        <div className={classes.translateBase} ref={this.childRef}>
          {childrenWithMatrix}
        </div>
      </div>
    )
  }
}

// @ts-ignore
export default withStyles(styles)(Zoomable)
